<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import Logo from '@/assets/Apexx_LOGO.webp';
import {
	getLastIdentity,
	resolveOtpContinueFromIdentity,
	type HubLastIdentity,
} from '@/auth/lastIdentity';
import { performHubLogout } from '@/auth/logout';
import { shouldUseNativeOtpApiLogin } from '@/auth/setup';
import { hubPinia } from '@/pinia';
import { useHubAuthStore } from '@/store/hubAuthStore';
import { isKeycloakLogoutInProgress } from '@apexx/auth';
import {
	Avatar,
	Button,
	Typography,
	Wizard,
	avatarBackgroundStyle,
	initialsFromName,
} from '@apexx/component-library';
import { motion } from 'motion-v';

definePage({ name: 'reauth', meta: { public: true } });

const authStore = useHubAuthStore(hubPinia);
const router = useRouter();

const identity = ref<HubLastIdentity | null>(null);
const isContinuing = ref(false);
const isLoggingOut = ref(false);
const loadError = ref<string>();

const displayName = computed(() => identity.value?.displayName ?? 'Signed out');
const subtitle = computed(() => {
	const otp = identity.value
		? resolveOtpContinueFromIdentity(identity.value)
		: null;
	if (otp) {
		return otp.identifier;
	}

	return identity.value?.email ?? identity.value?.phone ?? undefined;
});

const avatarInitials = computed(() => initialsFromName(displayName.value));
const avatarStyle = computed(() =>
	avatarBackgroundStyle(identity.value?.sub ?? displayName.value),
);

async function continueAsUser(): Promise<void> {
	if (!identity.value || isContinuing.value || isLoggingOut.value) {
		return;
	}

	isContinuing.value = true;
	try {
		const type = identity.value.userType;
		if (shouldUseNativeOtpApiLogin()) {
			const otp = resolveOtpContinueFromIdentity(identity.value);
			await router.push({
				name: 'login-otp',
				query: {
					type,
					from: 'reauth',
					...(otp
						? {
								identifier: otp.identifier,
								mode: otp.identifierMode,
								autoContinue: '1',
							}
						: {}),
				},
			});
			return;
		}

		await router.push({
			name: 'login-redirect',
			query: { type, from: 'reauth' },
		});
	} finally {
		isContinuing.value = false;
	}
}

async function logoutCompletely(): Promise<void> {
	if (isLoggingOut.value || isContinuing.value) {
		return;
	}

	isLoggingOut.value = true;
	try {
		await performHubLogout(router, {
			reason: 'user-sign-out',
			endSsoSession: true,
			redirectTo: 'login',
			clearIdentity: true,
		});
	} finally {
		isLoggingOut.value = false;
	}
}

onMounted(async () => {
	if (isKeycloakLogoutInProgress()) {
		return;
	}

	if (authStore.isLoggedIn) {
		await router.replace({ name: 'launcher' });
		return;
	}

	identity.value = await getLastIdentity();
	if (!identity.value) {
		await router.replace({ name: 'login' });
		return;
	}

	loadError.value = undefined;
});
</script>

<template>
	<Wizard
		content-padding="lg"
		centered
		root-class="min-h-dvh"
		content-class="relative pb-[calc(2.5rem+var(--safe-area-bottom))]"
	>
		<template #header>
			<div class="flex flex-col items-center justify-between gap-10 py-20">
				<img
					:src="Logo"
					alt="Apexx logo"
					class="h-16 overflow-hidden rounded"
				/>
			</div>
		</template>

		<motion.div
			:initial="{ opacity: 0, y: 20 }"
			:animate="{ opacity: 1, y: 0 }"
			:transition="{ duration: 0.3 }"
			class="mx-auto mt-6 flex w-full max-w-md grow flex-col gap-6"
		>
			<div class="flex flex-col items-center gap-3 text-center">
				<Typography variant="title" class="text-on-surface">
					Session expired.
				</Typography>
				<Typography variant="body" class="text-on-surface-muted">
					Sign in again to continue.
				</Typography>
			</div>

			<div
				v-if="identity"
				class="flex flex-col items-center gap-3 rounded-2xl bg-gray-50 px-6 py-8"
			>
				<Avatar
					size="lg"
					:initials="avatarInitials"
					:alt="displayName"
					:style="avatarStyle"
				/>
				<div class="flex flex-col items-center gap-1">
					<Typography variant="title" class="text-on-surface">
						{{ displayName }}
					</Typography>
					<Typography
						v-if="subtitle"
						variant="body"
						class="break-all text-on-surface-muted"
					>
						{{ subtitle }}
					</Typography>
					<Typography
						variant="label"
						class="mt-1 text-on-surface-muted"
					>
						Signed out
					</Typography>
				</div>
			</div>

			<div
				v-if="loadError"
				class="rounded-lg bg-error/10 px-4 py-3 text-left"
				role="alert"
			>
				<Typography variant="body" class="text-error">
					{{ loadError }}
				</Typography>
			</div>

			<Button
				variant="primary"
				size="lg"
				:full-width="true"
				class="text-white"
				:loading="isContinuing"
				:disabled="!identity || isLoggingOut"
				@click="continueAsUser"
			>
				Continue as {{ displayName }}
			</Button>

			<Button
				variant="ghost"
				size="lg"
				:full-width="true"
				class="border-0!"
				:loading="isLoggingOut"
				:disabled="isContinuing"
				@click="logoutCompletely"
			>
				Use a different account
			</Button>
		</motion.div>
	</Wizard>
</template>
